/**
 * Start a Quality run: a name, ONE collection, and optionally a subset of its
 * documents. Creates a `solution_run` of kind `quality`; the backend re-checks
 * access to the collection.
 */

import { useState } from "react";
import { Button, Group, Stack, Text, TextInput } from "@mantine/core";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { CollectionDocumentPicker, type CollectionSelection } from "@/features/solutions/shared";
import { createRun, type RunDetailOut } from "@/shared/api/solutionRuns";
import { toastApiError } from "@/shared/ui/toast";

/** Mirrors `MAX_DOCUMENTS` in `services/quality_alcoa.py`. */
export const MAX_QUALITY_DOCUMENTS = 25;

interface Props {
  onCreated: (run: RunDetailOut) => void;
}

export function QualityForm({ onCreated }: Props) {
  const getToken = useAccessToken();
  const [name, setName] = useState("");
  const [selection, setSelection] = useState<CollectionSelection[]>([]);
  const [busy, setBusy] = useState(false);

  const picked = selection[0];
  const subset = picked?.documentIds ?? null;
  const tooMany = subset !== null && subset.length > MAX_QUALITY_DOCUMENTS;
  const ready =
    name.trim() !== "" &&
    picked !== undefined &&
    (subset === null || subset.length > 0) &&
    !tooMany;

  const start = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      const token = await getToken();
      const run = await createRun(token, {
        kind: "quality",
        name: name.trim(),
        collections: [{ index_id: picked.indexId, document_ids: picked.documentIds }],
        upload_ids: [],
        params: {},
      });
      onCreated(run);
    } catch (err) {
      toastApiError("Could not start the quality check", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap="md" data-testid="quality-form">
      <TextInput
        label="Name"
        placeholder="e.g. Batch record XYZ-2603-017"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        required
      />
      <CollectionDocumentPicker
        value={selection}
        onChange={setSelection}
        label="Collection"
        multiple={false}
      />
      <Text size="xs" c="dimmed">
        Each document is read in full and scored against the nine ALCOA+ principles. Switch on “Only
        some documents” to check a subset (at most {MAX_QUALITY_DOCUMENTS} per run).
      </Text>
      {tooMany && (
        <Text size="sm" c="red">
          Select at most {MAX_QUALITY_DOCUMENTS} documents.
        </Text>
      )}
      <Group justify="flex-end">
        <Button onClick={() => void start()} disabled={!ready || busy} loading={busy}>
          Start quality check
        </Button>
      </Group>
    </Stack>
  );
}
